import type { Opportunity } from "@/hooks/api/use-opportunities";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { QueryParams } from "@/lib/process-query-params";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { type BaseListFilters, useListFilters } from "./use-list-filters";

// Default sort settings
const DEFAULT_SORT_COLUMN = "close_date";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

// SessionStorage key
const STORAGE_KEY = "opportunities::list::filters";

// Interface for filter configuration
export interface OpportunityFilters extends BaseListFilters {
  /**
   * Type identifier for Opportunity specific filters.
   * @private Internal use only - not meant to be accessed directly
   */
  readonly _type?: "opportunity";
}

export function useOpportunityListFilters() {
  // Helper function to build query params
  const buildQueryParams = (
    filters: OpportunityFilters,
    options: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<Opportunity>[];
      limit?: number;
    },
  ): QueryParams => {
    const { searchTerm, columns = [], limit = 200 } = options;

    // Get current sort values
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sortCol no longer exists in columns
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition for the current sort to get nullSort
    const column = columns.find((col) => col.key === sortCol);

    // Build base params
    const params: QueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir === null ? undefined : sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // -----------------------------------------------------------------
    // Build StructuredFilter array ➔ FilterGroup
    // -----------------------------------------------------------------

    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  };

  return useListFilters<
    OpportunityFilters,
    UnifiedTableColumn<Opportunity>,
    QueryParams
  >({
    storageKey: STORAGE_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });
}
